/**
 * ReportsArchive — зона хранения отчётов проекта.
 *
 * Точка входа в неё лежит в кабинете; сюда приходят за последним отчётом, а
 * старые держат на случай «а что было в марте». Поэтому страница разделена
 * надвое: витрина свежего документа наверху и список остального ниже.
 *
 * Список намеренно беден: период, балл с дельтой, деньги под риском,
 * состояние. Строка, которая пересказывает отчёт, перестаёт быть строкой, а
 * выбор в хранилище делают по периоду, а не по содержанию.
 *
 * Несобранные месяцы из списка не исчезают: у запланированного видно, когда
 * он придёт, у сорвавшегося — почему его нет. Молча пропущенный месяц
 * читается как «его и не должно было быть».
 *
 * Вёрстка ведётся по дому отчёта: реквизиты бейджами под титулом, заголовок
 * раздела с объяснением (`FloorHeading`), список — склеенный швом
 * `SplitCard`, а не россыпь отдельных карточек. Хранилище и документ —
 * соседние экраны одного маршрута, и разъезжаться в приёмах им нельзя.
 *
 * @format
 */

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';

import {Button} from 'ui/components/atoms/Button';
import {ChipMUI} from 'ui/components/atoms/ChipMUI';
import {NoticeCard} from 'ui/components/composition/NoticeCard';
import {PageHeader} from 'ui/components/composition/PageHeader';
import {Row} from 'ui/components/layout/Row';
import {Stack} from 'ui/components/layout/Stack';
import {spacingPx} from 'ui/components/layout/spacing';
import {AppHeader} from 'ui/components/organisms/AppHeader';
import {iconPxForRole} from 'ui/theme/typography';
import {surfaceVar} from 'ui/theme/surfaceContext';

import {AdSystemIcon} from '../ExecutiveReport/components/AdSystemIcon';
import {FloorHeading} from '../ExecutiveReport/components/FloorHeading';
import {SplitCard} from '../ExecutiveReport/components/SplitCard';
import {PAGE_MAX_WIDTH, pluralize} from '../ExecutiveReport/helpers';
import {LatestReport} from './components/LatestReport';
import {ReportRow} from './components/ReportRow';
import {PERIODS} from './helpers';
import {MOCK_ARCHIVE} from './mocks';
import type {ReportsArchiveData} from './types';

// Типографированный текст держим строками: NBSP прямо в JSX-детях валит
// линтер, а нужен он именно в отрендеренном тексте.
const REST_TITLE = 'Остальные периоды';
const REST_TITLE_ALONE = 'Периоды';
const REST_DESCRIPTION_PENDING =
  'Несобранные месяцы из списка не пропадают: у запланированного видно дату сборки, у сорвавшегося — причину.';
// Первая фраза уместна только там, где прошлые месяцы уже есть: на первом
// запуске список состоит из одного несобранного отчёта, и «за прошлые
// месяцы» было бы про пустоту.
const REST_DESCRIPTION_PAST = 'Отчёты за прошлые месяцы. ' + REST_DESCRIPTION_PENDING;
const EMPTY_TITLE = 'Отчётов пока нет';
const EMPTY_DESCRIPTION =
  'Первый отчёт соберётся, когда закроется текущий месяц. Он появится здесь, и мы напишем вам о нём.';
const LOADING_TITLE = 'Загружаем отчёты';
const LOADING_DESCRIPTION = 'Секунду\u00A0— забираем список из\u00A0хранилища.';
const ERROR_TITLE = 'Не удалось загрузить\u00A0список';
const ERROR_DESCRIPTION = 'Проверьте соединение и\u00A0попробуйте ещё\u00A0раз.';

/**
 * Кегль знака рекламной системы в бейдже реквизитов — тот же, по которому
 * `ChipMUI` размера `s` считает свои Material-иконки. Взято из шапки отчёта.
 */
const ICON_PX = iconPxForRole('label.m');

export interface ReportsArchiveProps {
  /** Содержимое хранилища. По умолчанию — кабинет, на котором его смотрели. */
  data?: ReportsArchiveData;
  /** Открыть отчёт. Без него строки остаются нечитаемыми ссылками — то есть текстом. */
  onOpenReport?: (id: string) => void;
  /** Возврат в кабинет. Не передан — строки навигации нет вовсе. */
  onBack?: () => void;
  /** Клик по логотипу в шапке приложения. */
  onLogoClick?: () => void;
  /** Данные ещё едут: вместо списка — карточка ожидания. */
  loading?: boolean;
  /** Запрос не удался: вместо списка — карточка отказа. */
  error?: boolean;
  /** Повторить запрос. Без него у карточки отказа нет кнопки. */
  onRetry?: () => void;
}

const Page = styled(Box)(({theme}) => ({
  backgroundColor: theme.palette.background.default,
  minHeight: '100vh',
  color: surfaceVar('onSurface'),
}));

const Content = styled(Stack)({
  maxWidth: PAGE_MAX_WIDTH,
  marginLeft: 'auto',
  marginRight: 'auto',
  paddingLeft: spacingPx('l'),
  paddingRight: spacingPx('l'),
  // Тот же удвоенный шаг, что и в самом отчёте: граница «хром приложения /
  // содержимое» крупнее, чем расстояние между блоками внутри.
  paddingTop: spacingPx('xl') * 2,
  paddingBottom: spacingPx('xl') * 2,
  // И тот же удвоенный шаг между блоками страницы: шапка, витрина и список —
  // это этажи, а `xl` (32) в отчёте разводит блоки ВНУТРИ этажа.
  gap: spacingPx('xl') * 2,
});

export function ReportsArchive({
  data = MOCK_ARCHIVE,
  onOpenReport,
  onBack,
  onLogoClick,
  loading = false,
  error = false,
  onRetry,
}: ReportsArchiveProps) {
  // Витрина — первый готовый отчёт. Если готовых нет (первый запуск, сорванная
  // сборка), витрины не будет вовсе: пустая рамка «здесь будет отчёт» занимает
  // экран и ничего не сообщает.
  const latest = data.entries.find(entry => entry.status === 'ready');
  const rest = data.entries.filter(entry => entry.id !== latest?.id);
  const isEmpty = data.entries.length === 0;

  return (
    <Page>
      <AppHeader variant='empty' onLogoClick={onLogoClick} />

      <Content>
        <Stack gap='m'>
          {onBack != null && (
            <Row>
              <Button variant='ghost' color='neutral' leftIcon='arrow_back' onClick={onBack}>
                К проекту
              </Button>
            </Row>
          )}

          <PageHeader title='Отчёты' />

          {/* Реквизиты бейджами, а не подзаголовком через точку: это два
              независимых факта, и разделитель между ними не нужен — его роль
              берёт на себя граница самого бейджа. Ровно так подписан и сам
              отчёт, поэтому переход между экранами не меняет грамматику. */}
          <Row gap='s' wrap='wrap' alignY='center'>
            {data.companyName != null && (
              <ChipMUI mode='badge' color='neutral' variant='outline' size='s'>
                {`Проект «${data.companyName}»`}
              </ChipMUI>
            )}
            {data.adSystem != null && (
              <ChipMUI
                mode='badge'
                color='neutral'
                variant='outline'
                size='s'
                leftIcon={<AdSystemIcon size={ICON_PX} />}
              >
                {data.adSystem}
              </ChipMUI>
            )}
          </Row>
        </Stack>

        {loading && (
          <NoticeCard tone='primary' title={LOADING_TITLE} description={LOADING_DESCRIPTION} />
        )}

        {!loading && error && (
          <NoticeCard
            tone='bad'
            title={ERROR_TITLE}
            description={ERROR_DESCRIPTION}
            action={
              onRetry != null && (
                <Button size='m' variant='outline' color='primary' onClick={onRetry}>
                  Повторить
                </Button>
              )
            }
          />
        )}

        {!loading && !error && (
          <>
            {isEmpty && (
              <NoticeCard tone='primary' title={EMPTY_TITLE} description={EMPTY_DESCRIPTION} />
            )}

            {latest != null && <LatestReport entry={latest} onOpen={onOpenReport} />}

            {rest.length > 0 && (
              <FloorHeading
                title={latest != null ? REST_TITLE : REST_TITLE_ALONE}
                description={latest != null ? REST_DESCRIPTION_PAST : REST_DESCRIPTION_PENDING}
                badges={
                  <ChipMUI mode='badge' color='neutral' variant='outline' size='s'>
                    {pluralize(rest.length, PERIODS)}
                  </ChipMUI>
                }
              >
                {/* Шов вместо зазора: строки — части одного списка, а не
                    самостоятельные карточки. `never` — потому что список и так
                    вертикальный, переворачивать на узком экране нечего. */}
                <SplitCard direction='column' reflowAt='never'>
                  {rest.map(entry => (
                    <ReportRow key={entry.id} entry={entry} onOpen={onOpenReport} />
                  ))}
                </SplitCard>
              </FloorHeading>
            )}
          </>
        )}
      </Content>
    </Page>
  );
}
